import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setValidationErrors,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import { LoadingState } from '../../../../../../common/global/models';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { DropdownOptionModel } from '../../../../models/Common';
import { ErrorState } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { customerService } from '../../../../services/customer';
import { formatAddressText } from '../common';

interface EditAccountInfoDialogProps {
  closeDialog: (reloadData?: boolean) => void;
  phoneNumber?: string;
  address?: DropdownOptionModel;
}

export const EditAccountInfoDialog = (props: EditAccountInfoDialogProps) => {
  const {
    closeDialog,
    phoneNumber: initialPhoneNumber,
    address: initialAddress,
  } = props;
  const { t: translate } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [address, setAddress] = useState(initialAddress);
  const [addressOptions, setAddressOptions] = useState<DropdownOptionModel[]>();
  const [saveButtonState, setSaveButtonState] = useState<LoadingState>(
    LoadingState.Ready,
  );
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});

  const loadAddressOptions = async (abortController?: AbortController) => {
    const response = await customerService.getAddressOptions({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      setAddressOptions(
        response.data.map(e => ({
          value: e.id,
          text: formatAddressText(e.addressSelector, e.address1),
        })),
      );
    } else {
      if (!abortController?.signal.aborted) {
        setShowErrorPanel(true);
      }
    }
  };

  const updateAccountInfo = async () => {
    setSaveButtonState(LoadingState.Requested);
    const response = await customerService.updateAccountInfo({
      addressId: address?.value,
      phoneNumber,
    });
    setSaveButtonState(LoadingState.Ready);

    if (!response.hasErrors) {
      closeDialog(true);
      showSuccessToast(translate(resources.saveConfirmation));
    } else {
      setValidationErrors(response, setErrors);
    }
  };

  useMount(loadAddressOptions);

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <Dialog closeDialog={closeDialog} showCloseIcon className="dialog-l">
      <Loader
        loading={!addressOptions?.length}
        loader={<LoadingSpinner />}
        showErrorPanel={showErrorPanel}
      >
        <div className="dialog-content">
          <div className="dialog-title">
            {translate(resources.accountInfo.edit.title)}
          </div>
          <Dropdown
            label={translate(resources.companyAddress)}
            data={addressOptions}
            dataItemKey="value"
            textField="text"
            value={address}
            onChange={e => setAddress(e.value)}
            errors={errors.address}
            clearErrors={() => clearErrors('address')}
          />
          <Input
            value={phoneNumber}
            label={translate(resources.phoneNumber)}
            onChange={e => setPhoneNumber(e.target.value?.toString())}
            errors={errors.phoneNumber}
            clearErrors={() => clearErrors('phoneNumber')}
          />
          <div className="info-box">
            <div className="info-icon">
              <span></span>
            </div>
            <div className="info-text">
              {translate(resources.accountInfo.edit.infoMessage)}
            </div>
          </div>
        </div>
        <div className="dialog-actions horizontal">
          <Button
            onClick={updateAccountInfo}
            className="btn btn-solid"
            loading={saveButtonState !== LoadingState.Ready}
          >
            <span className="btn-text">{translate(resources.save)}</span>
          </Button>
          <Button onClick={() => closeDialog()} className="btn btn-outline">
            <span className="btn-text"> {translate(resources.close)}</span>
          </Button>
        </div>
      </Loader>
    </Dialog>
  );
};
